/**
 * @file     filterFieldRegistry.ts
 * @purpose  THE canonical filter spec. One entry per filter field declares its
 *           URL key, how it serializes to / parses from the URL, and whether it
 *           is "active". Every consumer (the URL codec, the map's two fetch-URL
 *           builders, the is-any-filter-active check, and — from Phase 3 — the
 *           service predicate + cluster-bypass gate) derives from this list, so
 *           adding or changing a filter field is a single edit here instead of
 *           five hand-kept copies that drift.
 *
 *           Field ORDER in FILTER_FIELDS is the canonical URL serialization
 *           order; it intentionally matches the legacy filterUrlCodec statement
 *           order so the serialized query string is byte-identical.
 */

import { DEFAULT_VALUE, STATUSES, type TraditionalSearchValue } from './searchValue';

/** URL values are user input: anything outside the canonical status options
 *  (e.g. ?status=AppleTree) is dropped so it can't surface in the filter UI. */
const VALID_STATUS_VALUES = new Set(STATUSES.map((s) => s.value));

/** Same rule for sort: ?sort=garbage falls back to the default order. */
const VALID_SORT_VALUES = new Set<TraditionalSearchValue['sort']>([
  'newest',
  'price_asc',
  'price_desc',
  'beds_desc',
  'available_soonest',
]);

/** A rental-only boolean flag: serialized as '1', and only while mode is rent. */
function rentFlagField(key: 'petsAllowed' | 'furnishedOnly', urlKey: string): FilterFieldSpec {
  return {
    urlKey,
    serialize(sp, f) {
      if (f.mode === 'rent' && f[key]) sp.set(urlKey, '1');
    },
    applyParsed(draft, q) {
      if (q.get(urlKey) === '1') draft[key] = true;
      else delete draft[key];
    },
    isActive: (f) => f.mode === 'rent' && Boolean(f[key]),
    countsForUrlPresence: true,
  };
}

/** Minimal read surface shared by URLSearchParams and Next's ReadonlyURLSearchParams. */
export type QueryLike = {
  get(name: string): string | null;
  getAll(name: string): string[];
};

/** RPC argument name for the server cluster aggregation (mls_pin_clusters_filtered).
 *  A field WITHOUT a clusterParam cannot be expressed by the cluster RPC, so its
 *  presence forces the per-pin fallback (consumed in Phase 4). */
export type ClusterParam =
  | 'p_price_min' | 'p_price_max' | 'p_beds_min' | 'p_statuses' | 'p_baths_min'
  | 'p_dwelling_categories' | 'p_living_area_min' | 'p_living_area_max'
  | 'p_year_built_min' | 'p_year_built_max' | 'p_parking_min' | 'p_stories_min';

export interface FilterFieldSpec {
  /** The URL query-string key this field serializes to. */
  urlKey: string;
  /** Write this field's contribution into the params (no-op when empty/default). */
  serialize(sp: URLSearchParams, f: TraditionalSearchValue): void;
  /** Read this field from the query into a mutable filter draft. */
  applyParsed(draft: TraditionalSearchValue, q: QueryLike): void;
  /** True when this field imposes a non-default constraint. */
  isActive(f: TraditionalSearchValue): boolean;
  /** Whether this field counts toward "the URL carries a meaningful filter"
   *  (the codec's null-decision). */
  countsForUrlPresence: boolean;
  /** Cluster RPC arg, if the field can be pushed into server clustering. */
  clusterParam?: ClusterParam;
}

function queryNumber(q: QueryLike, key: string): number | undefined {
  const raw = q.get(key);
  if (raw == null || raw.trim() === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

/** A simple `set(String(value))` numeric field with a `!= null` active test. */
function numberField(
  key: keyof TraditionalSearchValue,
  urlKey: string,
  clusterParam?: ClusterParam,
): FilterFieldSpec {
  return {
    urlKey,
    serialize(sp, f) {
      const v = f[key] as number | undefined;
      if (v != null) sp.set(urlKey, String(v));
    },
    applyParsed(draft, q) {
      (draft[key] as number | undefined) = queryNumber(q, urlKey);
    },
    isActive(f) {
      return (f[key] as number | undefined) != null;
    },
    countsForUrlPresence: true,
    clusterParam,
  };
}

/** A single free-text field, trimmed on write, undefined when absent. */
function stringField(
  key: 'crossStreetFrom' | 'crossStreetTo',
  urlKey: string,
): FilterFieldSpec {
  return {
    urlKey,
    serialize(sp, f) {
      const v = f[key];
      if (v?.trim()) sp.set(urlKey, v.trim());
    },
    applyParsed(draft, q) {
      draft[key] = q.get(urlKey) ?? undefined;
    },
    isActive(f) {
      return Boolean(f[key]?.trim());
    },
    countsForUrlPresence: true,
  };
}

/** A repeated `append()` string-array field with a `length > 0` active test. */
function multiField(
  key: 'regions' | 'neighbourhoods' | 'postalCodes' | 'propertyTypes' | 'statuses' | 'pills',
  urlKey: string,
  opts: { countsForUrlPresence?: boolean; clusterParam?: ClusterParam } = {},
): FilterFieldSpec {
  return {
    urlKey,
    serialize(sp, f) {
      for (const v of f[key] ?? []) sp.append(urlKey, v);
    },
    applyParsed(draft, q) {
      draft[key] = q.getAll(urlKey);
    },
    isActive(f) {
      return (f[key] ?? []).length > 0;
    },
    countsForUrlPresence: opts.countsForUrlPresence ?? true,
    clusterParam: opts.clusterParam,
  };
}

/**
 * Canonical filter fields in URL serialization order. Mirrors the legacy
 * filterUrlCodec exactly so Phase 2 produces identical query strings.
 */
export const FILTER_FIELDS: FilterFieldSpec[] = [
  multiField('regions', 'region'),
  multiField('neighbourhoods', 'neighbourhood'),
  // postalCodes — FSA prefixes. No clusterParam: the server-cluster RPC can't
  // express a postal predicate, so an active postal filter bypasses server
  // clustering to the per-pin path (same as address).
  multiField('postalCodes', 'postalCode'),
  numberField('priceMin', 'priceMin', 'p_price_min'),
  numberField('priceMax', 'priceMax', 'p_price_max'),
  numberField('bedsMin', 'bedsMin', 'p_beds_min'),
  numberField('bathsMin', 'bathsMin', 'p_baths_min'),
  multiField('propertyTypes', 'propertyType', { clusterParam: 'p_dwelling_categories' }),
  numberField('livingAreaMin', 'livingAreaMin', 'p_living_area_min'),
  numberField('livingAreaMax', 'livingAreaMax', 'p_living_area_max'),
  numberField('yearBuiltMin', 'yearBuiltMin', 'p_year_built_min'),
  numberField('yearBuiltMax', 'yearBuiltMax', 'p_year_built_max'),
  numberField('lotSizeMin', 'lotSizeMin'),
  numberField('lotSizeMax', 'lotSizeMax'),
  numberField('parkingMin', 'parkingMin', 'p_parking_min'),
  numberField('storiesMin', 'storiesMin', 'p_stories_min'),
  numberField('streetNumberFrom', 'streetNumberFrom'),
  numberField('streetNumberTo', 'streetNumberTo'),
  stringField('crossStreetFrom', 'crossStreetFrom'),
  stringField('crossStreetTo', 'crossStreetTo'),
  // openHouseOnly — boolean flag, serialized as '1'.
  {
    urlKey: 'openHouseOnly',
    serialize(sp, f) {
      if (f.openHouseOnly) sp.set('openHouseOnly', '1');
    },
    applyParsed(draft, q) {
      draft.openHouseOnly = q.get('openHouseOnly') === '1';
    },
    isActive: (f) => Boolean(f.openHouseOnly),
    countsForUrlPresence: true,
  },
  // presaleOnly: boolean flag serialized as '1'. No clusterParam: the cluster
  // RPC reads the canonical view, presales live in their own view.
  {
    urlKey: 'presaleOnly',
    serialize(sp, f) {
      if (f.presaleOnly) sp.set('presaleOnly', '1');
    },
    applyParsed(draft, q) {
      if (q.get('presaleOnly') === '1') draft.presaleOnly = true;
      else delete draft.presaleOnly;
    },
    isActive: (f) => Boolean(f.presaleOnly),
    countsForUrlPresence: true,
  },
  // statuses — multi, but with a non-empty default (['Active']); active when it
  // diverges from that default.
  {
    urlKey: 'status',
    serialize(sp, f) {
      for (const s of f.statuses ?? []) sp.append('status', s);
    },
    applyParsed(draft, q) {
      const all = q.getAll('status').filter((s) => VALID_STATUS_VALUES.has(s));
      draft.statuses = all.length > 0 ? all : DEFAULT_VALUE.statuses;
    },
    isActive: (f) => (f.statuses ?? DEFAULT_VALUE.statuses).join('|') !== DEFAULT_VALUE.statuses.join('|'),
    countsForUrlPresence: true,
    clusterParam: 'p_statuses',
  },
  // sort — single enum, omitted from the URL when it is the default ('newest').
  {
    urlKey: 'sort',
    serialize(sp, f) {
      if (f.sort && f.sort !== 'newest') sp.set('sort', f.sort);
    },
    applyParsed(draft, q) {
      const raw = q.get('sort') as TraditionalSearchValue['sort'] | null;
      draft.sort = raw && VALID_SORT_VALUES.has(raw) ? raw : DEFAULT_VALUE.sort;
    },
    isActive: (f) => f.sort !== DEFAULT_VALUE.sort,
    countsForUrlPresence: true,
  },
  // keywords — comma-joined CSV in a single param.
  {
    urlKey: 'keywords',
    serialize(sp, f) {
      const kws = f.keywords ?? [];
      if (kws.length > 0) sp.set('keywords', kws.join(','));
    },
    applyParsed(draft, q) {
      draft.keywords =
        q
          .get('keywords')
          ?.split(',')
          .map((s) => s.trim())
          .filter(Boolean) ?? DEFAULT_VALUE.keywords;
    },
    isActive: (f) => (f.keywords ?? []).length > 0,
    countsForUrlPresence: true,
  },
  // pills: hub links (?pill=price-reduced) seed the search, so they count.
  multiField('pills', 'pill'),
  // address — free-text; trimmed on write, reads legacy `q` alias on parse.
  {
    urlKey: 'address',
    serialize(sp, f) {
      if (f.address?.trim()) sp.set('address', f.address.trim());
    },
    applyParsed(draft, q) {
      draft.address = q.get('address') ?? q.get('q') ?? DEFAULT_VALUE.address;
    },
    isActive: (f) => Boolean(f.address?.trim()),
    countsForUrlPresence: true,
  },
  // Potential monthly rent. Appended so existing query-string order is unchanged
  // when these are unset (map pin cache keys are the URL).
  numberField('rentMin', 'rentMin'),
  numberField('rentMax', 'rentMax'),
  // Rentals. Appended last so every sale URL (and its pins cache key) is
  // byte-identical. mode has no clusterParam: rentals come from their own view,
  // so a rental search always takes the per-pin path. The rental-only fields
  // serialize only while mode is rent, so a stale value can never narrow a
  // sale search.
  {
    urlKey: 'mode',
    serialize(sp, f) {
      if (f.mode === 'rent') sp.set('mode', 'rent');
    },
    applyParsed(draft, q) {
      if (q.get('mode') === 'rent') draft.mode = 'rent';
      else delete draft.mode;
    },
    isActive: (f) => f.mode === 'rent',
    countsForUrlPresence: true,
  },
  {
    urlKey: 'availableBy',
    serialize(sp, f) {
      if (f.mode === 'rent' && f.availableBy) sp.set('availableBy', f.availableBy);
    },
    applyParsed(draft, q) {
      const v = q.get('availableBy');
      if (v && /^\d{4}-\d{2}-\d{2}$/.test(v)) draft.availableBy = v;
      else delete draft.availableBy;
    },
    isActive: (f) => f.mode === 'rent' && Boolean(f.availableBy),
    countsForUrlPresence: true,
  },
  rentFlagField('petsAllowed', 'pets'),
  rentFlagField('furnishedOnly', 'furnished'),
  {
    urlKey: 'laundry',
    serialize(sp, f) {
      if (f.mode === 'rent' && f.inUnitLaundry) sp.set('laundry', 'in_unit');
    },
    applyParsed(draft, q) {
      if (q.get('laundry') === 'in_unit') draft.inUnitLaundry = true;
      else delete draft.inUnitLaundry;
    },
    isActive: (f) => f.mode === 'rent' && Boolean(f.inUnitLaundry),
    countsForUrlPresence: true,
  },
  {
    urlKey: 'term',
    serialize(sp, f) {
      if (f.mode === 'rent' && f.leaseTerm) sp.set('term', f.leaseTerm);
    },
    applyParsed(draft, q) {
      const v = q.get('term');
      if (v === 'short' || v === 'long') draft.leaseTerm = v;
      else delete draft.leaseTerm;
    },
    isActive: (f) => f.mode === 'rent' && Boolean(f.leaseTerm),
    countsForUrlPresence: true,
  },
];

/** urlKey → spec, for builders that serialize an explicit ordered subset. */
export const FIELD_BY_URL_KEY: Map<string, FilterFieldSpec> = new Map(
  FILTER_FIELDS.map((f) => [f.urlKey, f]),
);

/** Serialize the full canonical filter (replacement for filtersToSearchParams). */
/** The array fields. A filter object reaching a consumer with any of these
 *  missing is a crash, not a degraded filter — every consumer calls `.length`
 *  or iterates. */
const ARRAY_FIELDS = [
  'regions',
  'neighbourhoods',
  'postalCodes',
  'propertyTypes',
  'statuses',
  'keywords',
  'pills',
] as const satisfies readonly (keyof TraditionalSearchValue)[];

/**
 * Complete a partial / legacy filter object into a full TraditionalSearchValue.
 *
 * Filter objects arrive from producers that predate fields or drop them: the
 * localStorage blob (written before `keywords` and `statuses` existed), the
 * persisted DB filter-profile blob, and applyAiFiltersToMap (which assigns
 * `ai[k]` for every AI-controlled key, so an omitted key lands as `undefined`).
 * Normalize at the seam rather than making every reader defensive.
 */
export function normalizeFilterValue(
  partial: Partial<TraditionalSearchValue> | null | undefined,
): TraditionalSearchValue {
  const next: TraditionalSearchValue = { ...DEFAULT_VALUE, ...(partial ?? {}) };
  for (const key of ARRAY_FIELDS) {
    if (!Array.isArray(next[key])) next[key] = DEFAULT_VALUE[key];
  }
  if (typeof next.openHouseOnly !== 'boolean') next.openHouseOnly = DEFAULT_VALUE.openHouseOnly;
  if (!next.sort || !VALID_SORT_VALUES.has(next.sort)) next.sort = DEFAULT_VALUE.sort;
  return next;
}

export function serializeFilters(f: TraditionalSearchValue): URLSearchParams {
  const sp = new URLSearchParams();
  for (const field of FILTER_FIELDS) field.serialize(sp, f);
  return sp;
}

/** Serialize ONLY the given urlKeys, in the given order, into `sp`. Lets the map
 *  builders reuse per-field logic while keeping their historical param order. */
export function serializeFilterKeys(
  sp: URLSearchParams,
  f: TraditionalSearchValue,
  urlKeys: string[],
): void {
  for (const key of urlKeys) FIELD_BY_URL_KEY.get(key)?.serialize(sp, f);
}

/** Parse a query into a full filter draft (no null-decision). */
export function parseFilters(q: QueryLike): TraditionalSearchValue {
  const draft: TraditionalSearchValue = { ...DEFAULT_VALUE };
  for (const field of FILTER_FIELDS) field.applyParsed(draft, q);
  return draft;
}

/** True when any field imposes a non-default constraint (pills included). */
export function isAnyFilterActive(f: TraditionalSearchValue): boolean {
  return FILTER_FIELDS.some((field) => field.isActive(f));
}

/** True when the URL carries a filter meaningful enough to override stored state. */
export function hasUrlFilterPresence(f: TraditionalSearchValue): boolean {
  return FILTER_FIELDS.some((field) => field.countsForUrlPresence && field.isActive(f));
}

/**
 * True when every active filter can be expressed by the server cluster RPC
 * (mls_pin_clusters_filtered), i.e. no active field lacks a clusterParam. When
 * this is false the caller must fall back to the per-pin path, which filters on
 * everything. This is the single source for the map's "bypass server clusters"
 * decision, replacing a hand-kept boolean that drifted from this list.
 */
export function isServerClusterCompatible(f: TraditionalSearchValue): boolean {
  return FILTER_FIELDS.every((field) => !field.isActive(f) || field.clusterParam != null);
}
